// @tg-seq: 10794
// @architect-approved: HARDCODED_COLORS - User login UI with branded colors (may refactor to theme tokens)

// @tg-golden-rule: BEFORE debugging, validate inside-out (Layer 1-7).
// Read: docs/agent-contracts/AGENT_LEGO_BLOCK_GOLDEN_RULE.md

// Full astro login page — ported from MainStreet/Authentication/Login.tsx
// Multi-step auth: password → OTP → org selection
// Space theme: earth background, twinkling stars, caboose branding

import BusinessIcon from '@mui/icons-material/Business';
import GoogleIcon from '@mui/icons-material/Google';
import LockIcon from '@mui/icons-material/Lock';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useAuth } from '../../utils/useAuth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../../app/hooks';
import { setAccessToken, setPfUser, setUserOrganizations } from '../../app/redux/auth/authSlice';
import { getTsxFileName } from '../../utils/componentIdentity';
import { navigateBySeq } from '../../utils/directoryNavigation';
import cabooseLogo from '../RegisteredModules/img/bg_10794_01.png';
import earthImg from '../RegisteredModules/img/bg_10794_02.png';

import { SecureStorage } from '../../utils/SecureStorage';
// Auth flow steps
type AuthStep = 'login' | 'otp' | 'org-select';

interface Organization {
    organization_uuid: string;
    organization_name: string;
    role_id: number | null;
    is_primary: boolean;
}

const Self: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // Where the user lands after a successful login. ADM.PFUser has no
    // "preferred landing page" column yet (2026-10-22) - once one exists,
    // read it from the login response / pfUser profile here instead. Until
    // then every user lands on the platform's Welcome Center.
    const getPostLoginDestination = (): string => '/welcome';

    // Form state
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Multi-step auth state
    const [authStep, setAuthStep] = useState<AuthStep>('login');
    const [partialToken, setPartialToken] = useState<string>('');
    const [pfuserUuid, setPfuserUuid] = useState<string>('');

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/login`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        username: username || undefined,
                        email: username.includes('@') ? username : undefined,
                        password,
                        ttp_id: 0,
                    }),
                },
            );

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 401 && data.detail === 'Invalid credentials') {
                    toast.info('Account not found. Redirecting to sign up...', { autoClose: 2000 });
                    setTimeout(() => navigateBySeq(10023, 'component', navigate), 2000);
                    return;
                }
                throw new Error(data.detail || 'Login failed');
            }

            // Check if OTP is required
            if (data.otp_required) {
                setPartialToken(data.access_token);
                setPfuserUuid(data.pfuser_uuid);
                setUserOrganizationsState(data.organizations || []);
                setAuthStep('otp');
                toast.info('Please enter your OTP code');
                return;
            }

            // Check if org selection is required
            if (data.requires_org_selection && data.organizations?.length > 1) {
                setPfuserUuid(data.pfuser_uuid);
                setUserOrganizationsState(data.organizations);
                setPartialToken(data.access_token);
                setAuthStep('org-select');
                toast.info('Select your organization');
                return;
            }

            // Direct login success
            SecureStorage.setAccessToken(data.access_token);
            SecureStorage.setRefreshToken(data.refresh_token);
            dispatch(setAccessToken(data.access_token));

            // ✅ FIX: Persist PFUser identity so PlatformHeader can load org associations.
            // Previously only the access token was stored here - pfUser was never set in
            // Redux nor SecureStorage, so after a real logout (which clears localStorage)
            // PlatformHeader's restore-on-reload fallback had nothing to restore and bailed
            // out before calling /api/org-pfuser-association-master, leaving the Organization
            // dropdown permanently empty.
            SecureStorage.setUserContext({ pfuserUuid: data.pfuser_uuid, username: data.username });
            dispatch(
                setPfUser({ pfuser_id: 0, username: data.username, user_uuid: data.pfuser_uuid }),
            );

            // Auto-select single org
            if (data.organizations?.length === 1) {
                await handleOrganizationSelect(data.organizations[0].organization_uuid);
            } else {
                toast.success('Login successful! Welcome back.');
                navigate(getPostLoginDestination());
            }
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Invalid username or password');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/otp/verify`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        pfuser_uuid: pfuserUuid,
                        otp_code: otpCode,
                    }),
                },
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Invalid OTP code');
            }

            SecureStorage.setAccessToken(data.access_token);
            SecureStorage.setRefreshToken(data.refresh_token);
            dispatch(setAccessToken(data.access_token));

            // ✅ FIX: Same PFUser identity persistence as the direct-login success path above.
            SecureStorage.setUserContext({ pfuserUuid: pfuserUuid, username: data.username });
            dispatch(setPfUser({ pfuser_id: 0, username: data.username, user_uuid: pfuserUuid }));

            if (data.requires_org_selection && userOrganizations.length > 1) {
                setAuthStep('org-select');
                toast.info('Select your organization');
            } else if (userOrganizations.length === 1) {
                await handleOrganizationSelect(userOrganizations[0].organization_uuid);
            } else {
                toast.success('Login successful! Welcome back.');
                navigate(getPostLoginDestination());
            }
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Invalid OTP code');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOrganizationSelect = async (organization_uuid: string) => {
        setIsLoading(true);

        try {
            const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/select-organization`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${partialToken || SecureStorage.getAccessToken()}`,
                    },
                    body: JSON.stringify({
                        pfuser_uuid: pfuserUuid,
                        organization_uuid: organizationUuid,
                    }),
                },
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Organization selection failed');
            }

            SecureStorage.setAccessToken(data.access_token);
            localStorage.setItem('active_organization_uuid', organizationUuid);
            dispatch(setAccessToken(data.access_token));

            const selectedOrg = userOrganizations.find(
                (o) => o.organization_uuid === organizationUuid,
            );
            if (selectedOrg) {
                dispatch(
                    setUserOrganizations([
                        {
                            OrganizationId: 0,
                            OrganizationName: selectedOrg.organization_name,
                            Role: data.role_id?.toString(),
                        },
                    ]),
                );
            }

            toast.success(`Welcome to ${data.organization_name}!`);
            navigate(getPostLoginDestination());
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to select organization');
        } finally {
            setIsLoading(false);
        }
    };

    // Render auth step content
    const renderAuthContent = () => {
        switch (authStep) {
            case 'otp':
                return (
                    <Box component="form" onSubmit={handleOtpVerify} sx={{ width: '100%', mt: 2 }}>
                        <Alert
                            severity="info"
                            sx={{ mb: 2, background: 'rgba(33, 150, 243, 0.1)' }}
                        >
                            <Typography variant="body2" sx={{ color: 'white' }}>
                                <LockIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                                Enter the 6-digit code from your authenticator app
                            </Typography>
                        </Alert>
                        <TextField
                            required
                            fullWidth
                            id="otp-code"
                            label="OTP Code"
                            name="otp"
                            autoComplete="one-time-code"
                            autoFocus
                            value={otpCode}
                            onChange={(e) =>
                                setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                            }
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#131109',
                                    fontSize: '1.5rem',
                                    letterSpacing: '0.5rem',
                                    textAlign: 'center',
                                    '& fieldset': { borderColor: '#e7e6df' },
                                    '&:hover fieldset': { borderColor: '#1466ef' },
                                    '&.Mui-focused fieldset': { borderColor: '#1466ef' },
                                },
                                '& .MuiInputLabel-root': { color: 'rgba(19, 17, 9, 0.6)' },
                            }}
                            slotProps={{
                                htmlInput: { maxLength: 6, pattern: '[0-9]*' },
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={isLoading || otpCode.length !== 6}
                            sx={{
                                mt: 2,
                                py: 1.2,
                                fontSize: '1rem',
                                fontWeight: 600,
                                borderRadius: '6px',
                                background: '#1466ef',
                                color: '#fdfdf9',
                                '&:hover': {
                                    background: '#0d5dd9',
                                },
                            }}
                        >
                            {isLoading ? <CircularProgress size={24} /> : 'Verify OTP'}
                        </Button>
                        <Button
                            fullWidth
                            variant="text"
                            onClick={() => {
                                setAuthStep('login');
                                setOtpCode('');
                            }}
                            sx={{ mt: 1, color: 'rgba(19, 17, 9, 0.6)' }}
                        >
                            Back to Login
                        </Button>
                    </Box>
                );

            case 'org-select':
                return (
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <Alert
                            severity="info"
                            sx={{
                                mb: 2,
                                background: 'rgba(20, 102, 239, 0.08)',
                                border: '1px solid rgba(20, 102, 239, 0.2)',
                            }}
                        >
                            <Typography variant="body2" sx={{ color: '#131109' }}>
                                <BusinessIcon
                                    sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }}
                                />
                                Select which organization you want to access
                            </Typography>
                        </Alert>
                        <List sx={{ bgcolor: 'rgba(131, 121, 105, 0.08)', borderRadius: 2, p: 0 }}>
                            {userOrganizations.map((org, index) => (
                                <React.Fragment key={org.organization_uuid}>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() =>
                                                handleOrganizationSelect(org.organization_uuid)
                                            }
                                            disabled={isLoading}
                                            sx={{
                                                py: 2,
                                                '&:hover': {
                                                    background: 'rgba(20, 102, 239, 0.08)',
                                                },
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        sx={{
                                                            color: '#131109',
                                                            fontWeight: org.is_primary ? 700 : 400,
                                                        }}
                                                    >
                                                        {org.organization_name}
                                                        {org.is_primary && (
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    ml: 1,
                                                                    color: '#1466ef',
                                                                    fontSize: '0.8rem',
                                                                }}
                                                            >
                                                                (Primary)
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                }
                                                secondary={
                                                    org.role_id ? (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'rgba(19, 17, 9, 0.5)',
                                                            }}
                                                        >
                                                            Role ID: {org.role_id}
                                                        </Typography>
                                                    ) : null
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                    {index < userOrganizations.length - 1 && (
                                        <Divider sx={{ borderColor: 'rgba(131, 121, 105, 0.2)' }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </List>
                        <Button
                            fullWidth
                            variant="text"
                            onClick={() => {
                                setAuthStep('login');
                                setUserOrganizationsState([]);
                            }}
                            sx={{ mt: 2, color: 'rgba(19, 17, 9, 0.6)' }}
                        >
                            Back to Login
                        </Button>
                    </Box>
                );

            default: // login
                return (
                    <Box
                        component="form"
                        onSubmit={handleLogin}
                        sx={{
                            mt: 0.5,
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                        }}
                    >
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="username"
                            label="Username or Email"
                            name="username"
                            autoComplete="username"
                            autoFocus
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#131109',
                                    '& fieldset': { borderColor: '#e7e6df' },
                                    '&:hover fieldset': { borderColor: '#1466ef' },
                                    '&.Mui-focused fieldset': { borderColor: '#1466ef' },
                                },
                                '& .MuiInputLabel-root': { color: 'rgba(19, 17, 9, 0.6)' },
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#131109',
                                    '& fieldset': { borderColor: '#e7e6df' },
                                    '&:hover fieldset': { borderColor: '#1466ef' },
                                    '&.Mui-focused fieldset': { borderColor: '#1466ef' },
                                },
                                '& .MuiInputLabel-root': { color: 'rgba(19, 17, 9, 0.6)' },
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={isLoading}
                            sx={{
                                mt: 1,
                                mb: 1,
                                py: 1.2,
                                fontSize: '1rem',
                                fontWeight: 600,
                                borderRadius: '6px',
                                background: '#1466ef',
                                color: '#fdfdf9',
                                '&:hover': {
                                    background: '#0d5dd9',
                                },
                            }}
                        >
                            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                        </Button>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 2,
                                my: 0.5,
                                justifyContent: 'center',
                            }}
                        >
                            <Button
                                variant="outlined"
                                startIcon={<GoogleIcon />}
                                sx={{
                                    flex: 1,
                                    py: 0.8,
                                    color: '#131109',
                                    borderColor: '#e7e6df',
                                    '&:hover': {
                                        borderColor: '#1466ef',
                                        background: 'rgba(20, 102, 239, 0.05)',
                                    },
                                }}
                                onClick={() => toast.info('Google login coming soon!')}
                            >
                                Google
                            </Button>
                        </Box>
                        <Divider sx={{ my: 0.5, borderColor: 'rgba(131, 121, 105, 0.25)' }}>
                            <Typography
                                variant="caption"
                                sx={{ color: 'rgba(19, 17, 9, 0.5)', px: 1 }}
                            >
                                OR
                            </Typography>
                        </Divider>
                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: 'center',
                                color: '#131109',
                                fontWeight: 600,
                            }}
                        >
                            New to Techgacity?
                        </Typography>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<PersonAddAltIcon />}
                            sx={{
                                mb: 1,
                                py: 1.1,
                                fontSize: '1rem',
                                fontWeight: 700,
                                borderRadius: '6px',
                                background: '#0c7a44',
                                color: '#fdfdf9',
                                boxShadow: '0 2px 8px rgba(12, 122, 68, 0.35)',
                                '&:hover': {
                                    background: '#095f36',
                                    boxShadow: '0 4px 12px rgba(12, 122, 68, 0.45)',
                                },
                            }}
                            onClick={() => navigateBySeq(10023, 'component', navigate)}
                        >
                            Create Your Free Account
                        </Button>
                        <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    mb: 0.5,
                                    color: 'rgba(19, 17, 9, 0.6)',
                                    display: 'block',
                                }}
                            >
                                Traveling executive? Need quick access?
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                sx={{
                                    color: '#1466ef',
                                    borderColor: 'rgba(20, 102, 239, 0.3)',
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    '&:hover': {
                                        borderColor: '#1466ef',
                                        background: 'rgba(20, 102, 239, 0.05)',
                                    },
                                }}
                                onClick={() => navigate('/executive-access')}
                            >
                                Executive Quick Access
                            </Button>
                        </Box>
                    </Box>
                );
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                background: '#e7e6df', // Warm paper
                fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
            }}
        >
            {/* Subtle texture overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1,
                    backgroundImage:
                        'linear-gradient(rgba(131, 121, 105, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(131, 121, 105, 0.02) 1px, transparent 1px)',
                    backgroundSize: '24px 24px',
                    pointerEvents: 'none',
                }}
            />

            {/* Subtle floating dots */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 2,
                    pointerEvents: 'none',
                }}
            >
                {[...Array(20)].map((_, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            width: 3,
                            height: 3,
                            background: 'rgba(131, 121, 105, 0.15)',
                            borderRadius: '50%',
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
                            animationDelay: `${Math.random() * 3}s`,
                            '@keyframes float': {
                                '0%, 100%': { opacity: 0.2, transform: 'translateY(0px)' },
                                '50%': { opacity: 0.4, transform: 'translateY(-10px)' },
                            },
                        }}
                    />
                ))}
            </Box>

            <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 10 }}>
                <Paper
                    elevation={24}
                    sx={{
                        p: 3,
                        maxWidth: 450,
                        mx: 'auto',
                        borderRadius: '8px',
                        background: '#fdfdf9', // Card surface
                        border: '1px solid rgba(131, 121, 105, 0.2)',
                        boxShadow: '0 8px 32px rgba(27, 26, 23, 0.12)',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        {/* Caboose Logo */}
                        <Box
                            component="img"
                            src={cabooseLogo}
                            alt="Caboose Outcomes Logo"
                            sx={{
                                width: 160,
                                height: 'auto',
                                mb: 0.5,
                                display: 'block',
                                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.8))',
                            }}
                        />
                        <Typography
                            component="h1"
                            variant="h4"
                            sx={{
                                mb: 0.5,
                                color: '#131109', // Card ink
                                fontWeight: 700,
                                letterSpacing: '0.05em',
                            }}
                        >
                            Techgacity
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 2,
                                color: 'rgba(19, 17, 9, 0.6)',
                                textAlign: 'center',
                            }}
                        >
                            {authStep === 'otp'
                                ? 'Two-Factor Authentication'
                                : authStep === 'org-select'
                                  ? 'Select Your Organization'
                                  : 'Imagining a whole new world of business management.'}
                        </Typography>

                        {renderAuthContent()}
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

Self.displayName = getTsxFileName(import.meta.url);
export default Self;
